import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { ShieldAlert, Plus, Thermometer, TrendingDown, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import VisibilityPicker from "@/components/VisibilityPicker";

const CATEGORY_LABEL = {
  recebimento: "Recebimento",
  armazenamento: "Armazenamento",
  producao: "Produção",
  distribuicao: "Distribuição",
};

const emptyForm = {
  name: "",
  category: "armazenamento",
  description: "",
  control_limit_min: "",
  control_limit_max: "",
  unit: "°C",
  monitoring_frequency: "daily",
  assigned_roles: [],
  assigned_user_ids: [],
};

export default function APPCC() {
  const { user } = useAuth();
  const canManage = ["super_admin", "company_admin", "manager"].includes(user?.role);
  const [pccs, setPccs] = useState([]);
  const [monitorings, setMonitorings] = useState([]);
  const [users, setUsers] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [openMonitor, setOpenMonitor] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [reading, setReading] = useState({ value: "", corrective_action_taken: "", notes: "" });

  const load = async () => {
    try {
      const [p, m] = await Promise.all([
        api.get("/appcc/pccs"),
        api.get("/appcc/monitorings"),
      ]);
      setPccs(p.data);
      setMonitorings(m.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    load();
    if (canManage) {
      api
        .get("/users")
        .then(({ data }) => setUsers(data))
        .catch((err) => console.error("Failed to load users", err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage]);

  const create = async () => {
    if (!form.name.trim()) {
      toast.error("Nome obrigatório");
      return;
    }
    try {
      const payload = {
        ...form,
        control_limit_min: form.control_limit_min === "" ? null : Number(form.control_limit_min),
        control_limit_max: form.control_limit_max === "" ? null : Number(form.control_limit_max),
      };
      await api.post("/appcc/pccs", payload);
      toast.success("PCC criado");
      setOpenNew(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao criar");
    }
  };

  const submitReading = async () => {
    if (reading.value === "" || isNaN(Number(reading.value))) {
      toast.error("Informe um valor numérico");
      return;
    }
    try {
      const payload = {
        pcc_id: openMonitor.id,
        value: Number(reading.value),
        corrective_action_taken: reading.corrective_action_taken,
        notes: reading.notes,
      };
      const { data } = await api.post("/appcc/monitorings", payload);
      if (data.deviation) {
        toast.error("Desvio detectado! Ocorrência crítica aberta automaticamente.");
      } else {
        toast.success("Monitoramento registrado");
      }
      setOpenMonitor(null);
      setReading({ value: "", corrective_action_taken: "", notes: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao registrar");
    }
  };

  const remove = async (id) => {
    if (!confirm("Excluir este PCC?")) return;
    try {
      await api.delete(`/appcc/pccs/${id}`);
      toast.success("PCC removido");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha");
    }
  };

  const stats = useMemo(() => {
    const dev = monitorings.filter((m) => m.deviation).length;
    return {
      pccs: pccs.length,
      readings: monitorings.length,
      deviations: dev,
      ok: monitorings.length - dev,
    };
  }, [pccs, monitorings]);

  return (
    <Layout
      title="APPCC"
      subtitle="Pontos Críticos de Controle e monitoramento contínuo"
      actions={
        canManage ? (
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="appcc-new-pcc">
              <Plus className="h-4 w-4 mr-2" /> Novo PCC
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display">Novo PCC</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Nome *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex: Câmara fria 1 — Carnes"
                  className="mt-1 bg-white"
                  data-testid="appcc-name"
                />
              </div>
              <div>
                <Label>Descrição</Label>
                <Textarea
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="mt-1 bg-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Categoria</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="recebimento">Recebimento</SelectItem>
                      <SelectItem value="armazenamento">Armazenamento</SelectItem>
                      <SelectItem value="producao">Produção</SelectItem>
                      <SelectItem value="distribuicao">Distribuição</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Frequência</Label>
                  <Select value={form.monitoring_frequency} onValueChange={(v) => setForm({ ...form, monitoring_frequency: v })}>
                    <SelectTrigger className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hourly">Horária</SelectItem>
                      <SelectItem value="shift">Por turno</SelectItem>
                      <SelectItem value="daily">Diária</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Limite mín</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={form.control_limit_min}
                    onChange={(e) => setForm({ ...form, control_limit_min: e.target.value })}
                    className="mt-1 bg-white"
                    placeholder="-5"
                  />
                </div>
                <div>
                  <Label>Limite máx</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={form.control_limit_max}
                    onChange={(e) => setForm({ ...form, control_limit_max: e.target.value })}
                    className="mt-1 bg-white"
                    placeholder="5"
                  />
                </div>
                <div>
                  <Label>Unidade</Label>
                  <Input
                    value={form.unit}
                    onChange={(e) => setForm({ ...form, unit: e.target.value })}
                    className="mt-1 bg-white"
                  />
                </div>
              </div>
              <VisibilityPicker
                form={form}
                setForm={setForm}
                users={users}
                testPrefix="appcc-visibility"
                description="Quem verá esse PCC no dispositivo dos operadores."
              />
            </div>
            <DialogFooter>
              <Button onClick={create} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="appcc-submit">
                Criar PCC
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        ) : null
      }
    >
      <div data-testid="appcc-page" className="space-y-6">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "PCCs ativos", value: stats.pccs, icon: ShieldAlert, tone: "text-emerald-700 bg-emerald-100" },
            { label: "Leituras", value: stats.readings, icon: Thermometer, tone: "text-blue-700 bg-blue-100" },
            { label: "Conformes", value: stats.ok, icon: CheckCircle2, tone: "text-emerald-700 bg-emerald-100" },
            { label: "Desvios", value: stats.deviations, icon: TrendingDown, tone: "text-rose-700 bg-rose-100" },
          ].map((k) => (
            <Card key={k.label} className="p-5 rounded-xl border-stone-200 bg-white">
              <div className={`h-9 w-9 rounded-md flex items-center justify-center ${k.tone}`}>
                <k.icon className="h-4 w-4" />
              </div>
              <div className="mt-3 font-display text-3xl font-extrabold tracking-tight">{k.value}</div>
              <div className="text-xs text-stone-500 font-body uppercase tracking-wider mt-1">{k.label}</div>
            </Card>
          ))}
        </div>

        {/* PCCs table */}
        <Card className="rounded-xl border-stone-200 bg-white overflow-hidden">
          <div className="px-5 py-3 border-b border-stone-200">
            <h3 className="font-display font-bold tracking-tight">Pontos Críticos de Controle</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead>Nome</TableHead>
                <TableHead>Categoria</TableHead>
                <TableHead>Limites</TableHead>
                <TableHead>Frequência</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pccs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-stone-500 py-10">
                    Nenhum PCC cadastrado
                  </TableCell>
                </TableRow>
              )}
              {pccs.map((p) => (
                <TableRow key={p.id} data-testid={`pcc-row-${p.id}`}>
                  <TableCell>
                    <div className="font-display font-semibold">{p.name}</div>
                    <div className="text-xs text-stone-500 line-clamp-1">{p.description}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="border-emerald-200 text-emerald-800">
                      {CATEGORY_LABEL[p.category] || p.category}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-body text-sm">
                    {p.control_limit_min ?? "—"} {p.unit} → {p.control_limit_max ?? "—"} {p.unit}
                  </TableCell>
                  <TableCell className="font-body text-sm capitalize">{p.monitoring_frequency}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      className="mr-2"
                      onClick={() => setOpenMonitor(p)}
                      data-testid={`pcc-monitor-${p.id}`}
                    >
                      <Thermometer className="h-3.5 w-3.5 mr-1" /> Registrar leitura
                    </Button>
                    {canManage && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-rose-600"
                        onClick={() => remove(p.id)}
                      >
                        Excluir
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        {/* Recent monitorings */}
        <Card className="rounded-xl border-stone-200 bg-white overflow-hidden">
          <div className="px-5 py-3 border-b border-stone-200">
            <h3 className="font-display font-bold tracking-tight">Últimas leituras</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead>PCC</TableHead>
                <TableHead>Valor</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Operador</TableHead>
                <TableHead>Quando</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {monitorings.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-stone-500 py-10">
                    Nenhuma leitura registrada
                  </TableCell>
                </TableRow>
              )}
              {monitorings.slice(0, 30).map((m) => (
                <TableRow key={m.id} data-testid={`monitoring-row-${m.id}`}>
                  <TableCell className="font-display font-semibold">{m.pcc_name}</TableCell>
                  <TableCell className="font-body">{m.value}</TableCell>
                  <TableCell>
                    {m.deviation ? (
                      <Badge className="bg-rose-100 text-rose-800 border-none">Desvio</Badge>
                    ) : (
                      <Badge className="bg-emerald-100 text-emerald-800 border-none">Conforme</Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-body text-sm">{m.executor_name || "—"}</TableCell>
                  <TableCell className="font-body text-xs text-stone-500">{formatDateTime(m.measured_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        {/* Reading dialog */}
        <Dialog open={!!openMonitor} onOpenChange={(o) => !o && setOpenMonitor(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display">
                Registrar leitura — {openMonitor?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="rounded-md bg-stone-50 border border-stone-200 px-3 py-2 text-xs font-body text-stone-700">
                Limite aceitável: {openMonitor?.control_limit_min ?? "—"} {openMonitor?.unit} a {openMonitor?.control_limit_max ?? "—"} {openMonitor?.unit}
              </div>
              <div>
                <Label>Valor medido *</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={reading.value}
                  onChange={(e) => setReading({ ...reading, value: e.target.value })}
                  className="mt-1 bg-white"
                  data-testid="monitoring-value"
                  autoFocus
                />
              </div>
              <div>
                <Label>Ação corretiva (se houver desvio)</Label>
                <Textarea
                  rows={2}
                  value={reading.corrective_action_taken}
                  onChange={(e) => setReading({ ...reading, corrective_action_taken: e.target.value })}
                  className="mt-1 bg-white"
                />
              </div>
              <div>
                <Label>Observações</Label>
                <Textarea
                  rows={2}
                  value={reading.notes}
                  onChange={(e) => setReading({ ...reading, notes: e.target.value })}
                  className="mt-1 bg-white"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={submitReading}
                className="bg-emerald-800 hover:bg-emerald-900 text-white"
                data-testid="monitoring-submit"
              >
                Registrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
